<template>
  <div class="grid gap-5">
    <!-- Error state -->
    <div v-if="error" class="flex flex-col items-center justify-center h-[60vh] gap-3 px-6">
      <ExclamationTriangleIcon class="w-10 h-10 text-amber-500" />
      <p class="text-sm text-muted-foreground text-center">{{ $t('components.salesreport.server_error') }}</p>
    </div>

    <!-- Content -->
    <template v-else>
      <!-- Overview + Age side by side -->
      <div class="grid grid-cols-2 gap-5">
        <MembersOverviewCard
          :response="overviewResponse"
          :loading="overviewLoading"
        />

        <MembersAgeChart
          ref="ageChartRef"
          :response="ageResponse"
          :loading="ageLoading"
        />
      </div>

      <!-- Acquisition + Retention side by side -->
      <div class="grid grid-cols-2 gap-5">
        <div>
          <div class="flex flex-col items-center mb-3">
            <SegmentedPicker
              v-model="acquisitionAggregation"
              :groups="acquisitionPickerGroups"
            />
          </div>
          <MembersAcquisitionChart
            ref="acquisitionChartRef"
            :response="acquisitionResponse"
            :loading="acquisitionLoading"
            :format-x-label="acquisitionFormatXLabel"
          />
        </div>

        <div>
          <div class="flex flex-col items-center mb-3">
            <SegmentedPicker
              v-model="retentionWindow"
              :groups="retentionPickerGroups"
            />
          </div>
          <MembersRetentionHeatmap
            ref="retentionChartRef"
            :response="retentionResponse"
            :loading="retentionLoading"
          />
        </div>
      </div>

      <!-- Conversion vector -->
      <MembersConversionChart
        ref="conversionChartRef"
        :response="conversionResponse"
        :loading="conversionLoading"
      />

      <!-- Explorer Section -->
      <MembersExplorer
        v-if="appSettings.settings.experimentalFeatures"
        :center-id="centerId"
        :start-date="startDate"
        :end-date="endDate"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, ref, watch, nextTick, type Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { ExclamationTriangleIcon } from '@heroicons/vue/24/outline'
import { DateTime } from 'luxon'
import { useReportContext } from '../../../composables/useReportContext'
import { useTimeAggregation } from '../../../composables/useTimeAggregation'
import { useAppSettingsStore } from '../../../stores/AppSettingsStore'
import { MembersApi } from '../../../api/MembersApi'
import type { MembersStatsResponse, MemberDimension, MemberMetric, RetentionResponse } from '../../../responses'
import SegmentedPicker, { type PickerGroup } from '../../ui/SegmentedPicker.vue'
import MembersOverviewCard from '../../report-sections/MembersOverviewCard.vue'
import MembersAgeChart from '../../report-sections/MembersAgeChart.vue'
import MembersAcquisitionChart from '../../report-sections/MembersAcquisitionChart.vue'
import MembersRetentionHeatmap from '../../report-sections/MembersRetentionHeatmap.vue'
import MembersConversionChart from '../../report-sections/MembersConversionChart.vue'
import MembersExplorer from '../../report-sections/MembersExplorer.vue'

const { t } = useI18n()
const { card, centerId } = useReportContext()
const appSettings = useAppSettingsStore()

const error = ref(false)

// Conversion data is pre-fetched by ReportDesktopCard and provided via inject
const membersConversionData = inject<Ref<MembersStatsResponse | null>>('reportMembersConversionData')

// --- Date refs ---
const startDate = computed(() => card.value?.date?.startDate?.toJSDate() ?? new Date())
const endDate = computed(() => card.value?.date?.endDate?.toJSDate() ?? new Date())

// --- Aggregation for acquisition chart ---
const {
  aggregation: acquisitionAggregation,
  aggregationOptions: acquisitionAggOptions,
  formatXLabel: acquisitionFormatXLabel,
} = useTimeAggregation(startDate, endDate, { includeDistributions: false })

function buildPickerGroups(options: typeof acquisitionAggOptions.value): PickerGroup[] {
  const chronological = options.filter(o => o.group === 'chronological')
  const distribution = options.filter(o => o.group === 'distribution')
  const groups: PickerGroup[] = []
  if (chronological.length) {
    groups.push({
      label: t('time_aggregation.group_chronological'),
      options: chronological.map(o => ({ value: o.value, label: o.label })),
    })
  }
  if (distribution.length) {
    groups.push({
      label: t('time_aggregation.group_distribution'),
      options: distribution.map(o => ({ value: o.value, label: o.label })),
    })
  }
  return groups
}

const acquisitionPickerGroups = computed(() => buildPickerGroups(acquisitionAggOptions.value))
const retentionPickerGroups = computed((): PickerGroup[] => [{
  options: [
    { value: '6', label: t('report.members_retention_window_6') },
    { value: '12', label: t('report.members_retention_window_12') },
    { value: '24', label: t('report.members_retention_window_24') },
  ],
}])

// --- Response data ---
const overviewResponse = ref<MembersStatsResponse | null>(null)
const ageResponse = ref<MembersStatsResponse | null>(null)
const acquisitionResponse = ref<MembersStatsResponse | null>(null)
const retentionResponse = ref<RetentionResponse | null>(null)
const conversionResponse = ref<MembersStatsResponse | null>(null)
const conversionLoading = ref(false)

// --- Per-chart loading ---
const overviewLoading = ref(true)
const ageLoading = ref(true)
const acquisitionLoading = ref(true)
const retentionLoading = ref(true)
const retentionWindow = ref('12')

// --- Chart component refs ---
const ageChartRef = ref<InstanceType<typeof MembersAgeChart>>()
const acquisitionChartRef = ref<InstanceType<typeof MembersAcquisitionChart>>()
const retentionChartRef = ref<InstanceType<typeof MembersRetentionHeatmap>>()
const conversionChartRef = ref<InstanceType<typeof MembersConversionChart>>()

// --- Generic fetch helper ---

async function fetchChart(
  dimensions: MemberDimension[],
  metrics: MemberMetric[],
  intervalOverride?: string
): Promise<MembersStatsResponse> {
  if (!card.value) throw new Error('No card')
  return MembersApi.fetchMembersStats(
    card.value.date.startDate,
    card.value.date.endDate,
    (intervalOverride ?? card.value.interval) as any,
    centerId,
    dimensions,
    metrics,
    card.value.previousDate?.startDate,
    card.value.previousDate?.endDate
  )
}

async function fetchRetention() {
  retentionLoading.value = true
  try {
    const to = DateTime.now()
    const from = to.minus({ months: parseInt(retentionWindow.value) })
    retentionResponse.value = await MembersApi.fetchRetention(centerId, from, to)
    await nextTick()
    await nextTick()
    retentionChartRef.value?.refreshChart()
  } catch {
    retentionResponse.value = null
  } finally {
    retentionLoading.value = false
  }
}

// --- Watchers ---

watch(acquisitionAggregation, async (newAgg) => {
  acquisitionLoading.value = true
  try {
    acquisitionResponse.value = await fetchChart(['registration_month'], ['count'], newAgg)
    await nextTick()
    acquisitionChartRef.value?.refreshChart()
  } catch { /* silent */ }
  finally { acquisitionLoading.value = false }
})

watch(retentionWindow, () => {
  fetchRetention()
})

// Use conversion data from inject (pre-fetched by ReportDesktopCard)
if (membersConversionData?.value) {
  conversionResponse.value = membersConversionData.value
  conversionLoading.value = false
}

watch(() => membersConversionData?.value, (newData) => {
  if (newData) {
    conversionResponse.value = newData
    conversionLoading.value = false
    nextTick().then(() => conversionChartRef.value?.refreshChart())
  }
})

// --- Mount: fire all fetches in parallel ---

// Retention is autonomous (not tied to the report period)
fetchRetention()

// Fire all chart fetches in parallel
Promise.all([
  (async () => {
    try {
      overviewResponse.value = await fetchChart(['active'], ['count'])
    } catch { /* silent */ }
    finally { overviewLoading.value = false }
  })(),
  (async () => {
    try {
      ageResponse.value = await fetchChart(['age_range'], ['count'])
      await nextTick()
      ageChartRef.value?.refreshChart()
    } catch { /* silent */ }
    finally { ageLoading.value = false }
  })(),
  (async () => {
    try {
      acquisitionResponse.value = await fetchChart(['registration_month'], ['count'])
      await nextTick()
      acquisitionChartRef.value?.refreshChart()
    } catch { /* silent */ }
    finally { acquisitionLoading.value = false }
  })(),
  (async () => {
    // If conversion data is not yet available from inject, fetch it directly
    if (!membersConversionData?.value) {
      conversionLoading.value = true
      try {
        conversionResponse.value = await fetchChart(['first_activity'], ['count'])
        await nextTick()
        conversionChartRef.value?.refreshChart()
      } catch { /* silent */ }
      finally { conversionLoading.value = false }
    }
  })(),
]).catch(() => {
  error.value = true
})
</script>
